import api from './api';

// Admin Dashboard Service
export const adminService = {
  // ============================================================================
  // DASHBOARD STATISTICS
  // ============================================================================
  
  async getDashboardStats() {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },

  // ============================================================================
  // STUDENT MANAGEMENT
  // ============================================================================
  
  async getStudents(params?: {
    page?: number;
    limit?: number;
    search?: string;
    grade?: number;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const response = await api.get('/admin/students', { params });
    return response.data;
  },

  async getStudent(id: string) {
    const response = await api.get(`/admin/students/${id}`);
    return response.data;
  },

  async updateStudent(id: string, data: any) {
    const response = await api.put(`/admin/students/${id}`, data);
    return response.data;
  },

  async deleteStudent(id: string) {
    const response = await api.delete(`/admin/students/${id}`);
    return response.data;
  },

  async bulkCreateStudents(data: { students: { name: string }[]; grade: number; section?: string }) {
    // bcrypt hashing per student can be slow for large classes
    const response = await api.post('/admin/students/bulk-create', data, { timeout: 120000 });
    return response.data;
  },

  // ============================================================================
  // QUESTION MANAGEMENT
  // ============================================================================
  
  async getQuestions(params?: {
    page?: number;
    limit?: number;
    subject?: string;
    grade?: number;
    topic?: string;
    difficulty?: number;
    aiGenerated?: boolean;
    verified?: boolean;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const response = await api.get('/admin/questions', { params });
    return response.data;
  },

  async getQuestion(id: string) {
    const response = await api.get(`/admin/questions/${id}`);
    return response.data;
  },

  async createQuestion(data: any) {
    const response = await api.post('/admin/questions', data);
    return response.data;
  },

  async updateQuestion(id: string, data: any) {
    const response = await api.put(`/admin/questions/${id}`, data);
    return response.data;
  },

  async deleteQuestion(id: string) {
    const response = await api.delete(`/admin/questions/${id}`);
    return response.data;
  },

  // ============================================================================
  // REVENUE & SUBSCRIPTION MANAGEMENT
  // ============================================================================
  
  async getRevenueAnalytics() {
    const response = await api.get('/admin/revenue/analytics');
    return response.data;
  },

  async getSubscriptions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    plan?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const response = await api.get('/admin/subscriptions', { params });
    return response.data;
  },

  async getPayments(params?: {
    page?: number;
    limit?: number;
    status?: string;
    plan?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const response = await api.get('/admin/payments', { params });
    return response.data;
  },

  // ============================================================================
  // TEST SESSION MANAGEMENT
  // ============================================================================
  
  async getSessions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    subject?: string;
    grade?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const response = await api.get('/admin/sessions', { params });
    return response.data;
  },

  async getSession(id: string) {
    const response = await api.get(`/admin/sessions/${id}`);
    return response.data;
  },

  // ============================================================================
  // QUESTION GENERATION
  // ============================================================================
  
  async generateQuestions(data: {
    subject: string;
    grade: number;
    difficultyLevel?: number;
    count?: number;
  }) {
    // AI question generation can take a long time, so increase timeout
    const response = await api.post('/admin/generate-questions', data, {
      timeout: 120000, // 2 minutes timeout for AI generation
    });
    return response.data;
  },

  async bulkGenerateQuestions(data: {
    subjects?: string[];
    grades?: number[];
    difficultyLevels?: number[];
    questionsPerBatch?: number;
  }) {
    // Bulk generation can take even longer, so increase timeout further
    const response = await api.post('/admin/bulk-generate', data, {
      timeout: 300000, // 5 minutes timeout for bulk generation
    });
    return response.data;
  },

  async getQuestionStats(grade?: number) {
    const response = await api.get('/admin/question-stats', {
      params: grade ? { grade } : {}
    });
    return response.data;
  },

  // Legacy routes for backward compatibility
  async getQuestionsBySubjectGrade(subject: string, grade: number, params?: {
    page?: number;
    limit?: number;
  }) {
    const response = await api.get(`/admin/questions/${subject}/${grade}`, { params });
    return response.data;
  },

  async deleteQuestionsBySubjectGrade(subject: string, grade: number, aiGenerated?: boolean) {
    const response = await api.delete(`/admin/questions/${subject}/${grade}`, {
      params: { aiGenerated }
    });
    return response.data;
  }
};

// TypeScript interfaces for better type safety
export interface DashboardStats {
  overview: {
    totalStudents: number;
    totalQuestions: number;
    totalTests: number;
    activeTests: number;
    avgScore: number;
    avgAccuracy: number;
  };
  growth: {
    studentsToday: number;
    studentsWeek: number;
    studentsMonth: number;
    studentsYear: number;
    testsToday: number;
    testsWeek: number;
    testsMonth: number;
    testsYear: number;
  };
  distribution: {
    questionsBySubject: Array<{ _id: string; count: number }>;
    popularSubjects: Array<{ _id: string; count: number }>;
    gradeDistribution: Array<{ _id: number; count: number }>;
  };
  activity: {
    recentTests: Array<{
      student: { name: string; grade: number };
      subject: string;
      score: number;
      endTime: string;
    }>;
    topPerformers: Array<{
      name: string;
      grade: number;
      avgScore: number;
      testCount: number;
    }>;
  };
}

export interface AdminStudent {
  _id: string;
  name: string;
  email: string;
  grade: number;
  school: string;
  schoolRegistrationNumber: string;
  phone: string;
  parentName: string;
  parentPhone: string;
  isActive: boolean;
  role: 'student' | 'admin';
  registrationDate: string;
  lastLoginDate?: string;
  stats: {
    totalTests: number;
    averageScore: number;
    totalTimeSpent: number;
    streak: number;
    lastTestDate?: string;
    bestScore: number;
  };
}

export interface AdminQuestion {
  _id: string;
  subject: string;
  grade: number;
  questionText: string;
  questionType: 'multiple_choice' | 'true_false' | 'short_answer' | 'fill_blank';
  options: string[];
  correctAnswer: string;
  explanation?: string;
  topic: string;
  subtopic?: string;
  difficultyLevel: number;
  difficultyPercentage: number;
  aiGenerated: boolean;
  verified: boolean;
  isActive: boolean;
  tags: string[];
  stats: {
    timesAsked: number;
    correctAnswers: number;
    averageTimeToAnswer: number;
    difficultyRating: number;
  };
  createdAt: string;
  updatedAt: string;
  lastUpdated: string;
}

export interface AdminSession {
  _id: string;
  sessionId: string;
  student: {
    _id: string;
    name: string;
    email: string;
    grade: number;
  };
  subject: string;
  grade: number;
  status: 'active' | 'completed' | 'paused' | 'abandoned';
  score: number;
  accuracyRate: number;
  currentDifficultyLevel: number;
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  startTime: string;
  endTime?: string;
  duration?: number;
  analytics: {
    averageTimePerQuestion: number;
    fastestAnswer: number;
    slowestAnswer: number;
    topicPerformance: Array<{
      topic: string;
      correct: number;
      total: number;
      averageTime: number;
    }>;
  };
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalStudents?: number;
  totalQuestions?: number;
  totalSessions?: number;
  hasMore: boolean;
}

export default adminService; 